import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDevice, getDevicePrices } from '../services/devices';
import { getBattery } from '../services/batteries';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { DeviceThumb } from '../components/ui/DeviceThumb';
import { SpecSheetCard } from '../components/ui/SpecSheetCard';
import { VendorTable } from '../components/ui/VendorTable';
import { TrackDeviceButton } from '../components/ui/TrackDeviceButton';
import { useAuth } from '../hooks/useAuth';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();

  const deviceQ = useQuery({
    queryKey: ['device', id],
    queryFn: () => getDevice(id!),
    enabled: !!id,
  });

  const batteryQ = useQuery({
    queryKey: ['battery', deviceQ.data?.batteryId],
    queryFn: () => getBattery(deviceQ.data!.batteryId),
    enabled: !!deviceQ.data?.batteryId,
  });

  const pricesQ = useQuery({
    queryKey: ['device', id, 'prices'],
    queryFn: () => getDevicePrices(id!),
    enabled: !!id,
  });

  if (deviceQ.isLoading) return <Spinner />;
  if (deviceQ.isError || !deviceQ.data) {
    return <Card className="text-accent-red">Device not found.</Card>;
  }

  const device = deviceQ.data;

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={isAuthenticated ? '/me/devices' : '/'}
        className="text-sm text-neutral-400 hover:text-lime-400 w-fit"
      >
        ← {isAuthenticated ? 'Back to your devices' : 'Back to search'}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
        <div className="flex flex-col gap-4">
          <DeviceThumb imageUrl={device.imageUrl} alt={device.name} className="aspect-square" />
          <div>
            <div className="text-xs uppercase text-neutral-400">{device.brand}</div>
            <h1 className="text-2xl">{device.name}</h1>
            <div className="mt-2 inline-block text-xs bg-surface-700 rounded-full px-3 py-1 text-neutral-300">
              {device.category}
            </div>
          </div>

          <TrackDeviceButton deviceId={device._id} />

          {device.replacementGuideUrl && (
            <a
              href={device.replacementGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="card hover:border-lime-500 transition-colors"
            >
              <div className="font-semibold mb-1">How to replace</div>
              <div className="text-xs text-neutral-400">Click to view the guide →</div>
            </a>
          )}

          {batteryQ.isSuccess && <SpecSheetCard battery={batteryQ.data} />}
          {batteryQ.isLoading && <Spinner />}
        </div>

        <div className="flex flex-col gap-6">
          {pricesQ.isLoading && <Spinner />}
          {pricesQ.isError && (
            <Card className="text-accent-red">Could not load vendor prices.</Card>
          )}
          {pricesQ.isSuccess && batteryQ.data && (
            <VendorTable
              title={device.name}
              description={`In the list below, you can find the best prices for a compatible ${batteryQ.data.modelCode} battery.`}
              prices={pricesQ.data}
            />
          )}
        </div>
      </div>
    </div>
  );
}
